import type {
  AiAgentTemplate,
  BreakdownCategory,
  CharacterRole,
  PageSetup,
  ProjectFormat,
  ProjectRole,
  ProjectStatus,
  ProductionStage,
  ProductionStatus,
  RevisionColor,
  ScreenplayStyle,
  StructureStatus,
} from "./types.js";

export interface Localized {
  ru: string;
  en: string;
}

export const STYLE_META: Record<
  ScreenplayStyle,
  {
    label: Localized;
    short: Localized;
    description: Localized;
    shortcut?: string;
    caps?: boolean;
    className: string;
  }
> = {
  sceneHeading: {
    label: { ru: "Место и время", en: "Scene heading" },
    short: { ru: "Сцена", en: "Scene" },
    description: {
      ru: "ИНТ./НАТ., локация и время действия. Нумеруется автоматически.",
      en: "INT./EXT., location and time of day. Numbered automatically.",
    },
    shortcut: "Ctrl+1",
    caps: true,
    className: "sp-scene-heading",
  },
  action: {
    label: { ru: "Описание действия", en: "Action" },
    short: { ru: "Действие", en: "Action" },
    description: {
      ru: "Что происходит в кадре: действия, атмосфера, визуальные детали.",
      en: "What happens on screen: action, atmosphere, visual detail.",
    },
    shortcut: "Ctrl+3",
    className: "sp-action",
  },
  character: {
    label: { ru: "Персонаж", en: "Character" },
    short: { ru: "Персонаж", en: "Character" },
    description: {
      ru: "Имя говорящего. Ставится над репликой.",
      en: "Speaker cue above dialogue.",
    },
    shortcut: "Ctrl+4",
    caps: true,
    className: "sp-character",
  },
  parenthetical: {
    label: { ru: "Ремарка", en: "Parenthetical" },
    short: { ru: "Ремарка", en: "Parenthetical" },
    description: {
      ru: "Игра, интонация или адресат внутри реплики: (шёпотом), (в трубку).",
      en: "Delivery or direction inside dialogue: (whispering), (into phone).",
    },
    shortcut: "Ctrl+5",
    className: "sp-parenthetical",
  },
  dialogue: {
    label: { ru: "Реплика", en: "Dialogue" },
    short: { ru: "Реплика", en: "Dialogue" },
    description: {
      ru: "Текст, который произносит персонаж.",
      en: "The spoken line.",
    },
    shortcut: "Ctrl+6",
    className: "sp-dialogue",
  },
  lyrics: {
    label: { ru: "Песня", en: "Lyrics" },
    short: { ru: "Песня", en: "Lyrics" },
    description: {
      ru: "Текст песни или вокального номера.",
      en: "Song or vocal number lyrics.",
    },
    shortcut: "Ctrl+7",
    className: "sp-lyrics",
  },
  transition: {
    label: { ru: "Переход", en: "Transition" },
    short: { ru: "Переход", en: "Transition" },
    description: {
      ru: "Монтажный переход: CUT TO:, ЗАТЕМНЕНИЕ, НАПЛЫВ.",
      en: "Editorial transition: CUT TO:, FADE OUT, DISSOLVE TO:.",
    },
    shortcut: "Ctrl+9",
    caps: true,
    className: "sp-transition",
  },
  shot: {
    label: { ru: "Кадр", en: "Shot" },
    short: { ru: "Кадр", en: "Shot" },
    description: {
      ru: "Крупный план, общий план, наплыв камеры — производственная заметка в сценарии.",
      en: "Close-up, wide, camera move — a production-facing shot line.",
    },
    shortcut: "Ctrl+8",
    caps: true,
    className: "sp-shot",
  },
  sceneCharacters: {
    label: { ru: "Участники сцены", en: "Scene characters" },
    short: { ru: "Участники", en: "Cast" },
    description: {
      ru: "Список персонажей, присутствующих в сцене.",
      en: "Characters present in the scene.",
    },
    shortcut: "Ctrl+2",
    className: "sp-scene-characters",
  },
  inlineNote: {
    label: { ru: "Заметка в тексте", en: "Inline note" },
    short: { ru: "Заметка", en: "Note" },
    description: {
      ru: "Заметка автора или редактора, не попадает в печатную версию по умолчанию.",
      en: "Author/editor note, excluded from print by default.",
    },
    shortcut: "Ctrl+Shift+0",
    className: "sp-inline-note",
  },
  sequenceHeading: {
    label: { ru: "Последовательность", en: "Sequence heading" },
    short: { ru: "Последовательность", en: "Sequence" },
    description: {
      ru: "Крупная структурная единица внутри акта.",
      en: "A structural unit inside an act.",
    },
    shortcut: "Ctrl+Space",
    caps: true,
    className: "sp-sequence-heading",
  },
  actHeading: {
    label: { ru: "Акт", en: "Act heading" },
    short: { ru: "Акт", en: "Act" },
    description: {
      ru: "Акт или часть истории.",
      en: "Act or story part.",
    },
    shortcut: "Ctrl+Shift+Space",
    caps: true,
    className: "sp-act-heading",
  },
  beatHeading: {
    label: { ru: "Бит", en: "Beat" },
    short: { ru: "Бит", en: "Beat" },
    description: {
      ru: "Поворотный момент или эмоциональный бит внутри сцены.",
      en: "Turning point or emotional beat within a scene.",
    },
    shortcut: "Ctrl+Shift+1",
    caps: true,
    className: "sp-beat-heading",
  },
  unformattedText: {
    label: { ru: "Свободный текст", en: "Unformatted" },
    short: { ru: "Текст", en: "Text" },
    description: {
      ru: "Без сценарного форматирования.",
      en: "No screenplay formatting.",
    },
    shortcut: "Ctrl+0",
    className: "sp-unformatted",
  },
  pageBreak: {
    label: { ru: "Разрыв страницы", en: "Page break" },
    short: { ru: "Разрыв", en: "Break" },
    description: {
      ru: "Принудительный переход на новую страницу.",
      en: "Force a new page.",
    },
    className: "sp-page-break",
  },
};

export const REVISION_COLORS: Record<
  RevisionColor,
  { label: Localized; hex: string; text: string; description: Localized }
> = {
  none: {
    label: { ru: "Чистовик", en: "Clean" },
    hex: "#8b93a1",
    text: "#e7e9ee",
    description: { ru: "Без пометки правки", en: "No revision mark" },
  },
  blue: {
    label: { ru: "Синий", en: "Blue" },
    hex: "#5aa9ff",
    text: "#07131f",
    description: { ru: "Первая правка", en: "First revision" },
  },
  pink: {
    label: { ru: "Розовый", en: "Pink" },
    hex: "#ff8fbf",
    text: "#240814",
    description: { ru: "Вторая правка", en: "Second revision" },
  },
  green: {
    label: { ru: "Зелёный", en: "Green" },
    hex: "#62d6a3",
    text: "#04150e",
    description: { ru: "Третья правка", en: "Third revision" },
  },
  yellow: {
    label: { ru: "Жёлтый", en: "Yellow" },
    hex: "#f7d45a",
    text: "#1c1503",
    description: { ru: "Четвёртая правка", en: "Fourth revision" },
  },
  goldenrod: {
    label: { ru: "Золотой", en: "Goldenrod" },
    hex: "#d9a441",
    text: "#1a1204",
    description: { ru: "Пятая правка", en: "Fifth revision" },
  },
  buff: {
    label: { ru: "Бежевый", en: "Buff" },
    hex: "#e8d5a8",
    text: "#231b09",
    description: { ru: "Шестая правка", en: "Sixth revision" },
  },
  salmon: {
    label: { ru: "Лососевый", en: "Salmon" },
    hex: "#ff8f7a",
    text: "#220a06",
    description: { ru: "Седьмая правка", en: "Seventh revision" },
  },
  cherry: {
    label: { ru: "Вишнёвый", en: "Cherry" },
    hex: "#e2556f",
    text: "#fff4f6",
    description: { ru: "Восьмая правка", en: "Eighth revision" },
  },
  tan: {
    label: { ru: "Песочный", en: "Tan" },
    hex: "#d8bb8f",
    text: "#21170a",
    description: { ru: "Девятая правка", en: "Ninth revision" },
  },
  gray: {
    label: { ru: "Серый", en: "Gray" },
    hex: "#b9c0cc",
    text: "#14171c",
    description: { ru: "Нейтральная пометка", en: "Neutral mark" },
  },
};

export const REVISION_ORDER: RevisionColor[] = [
  "none",
  "blue",
  "pink",
  "green",
  "yellow",
  "goldenrod",
  "buff",
  "salmon",
  "cherry",
  "tan",
  "gray",
];

export const PROJECT_FORMAT_META: Record<
  ProjectFormat,
  { label: Localized; description: Localized; icon: string; accent: string; defaultDuration: string }
> = {
  series: {
    label: { ru: "Сериал", en: "Series" },
    description: {
      ru: "Многосерийная драма, комедия или документальный цикл.",
      en: "Multi-episode drama, comedy or documentary series.",
    },
    icon: "clapperboard",
    accent: "#f0a93b",
    defaultDuration: "8 × 45 мин",
  },
  screenplay: {
    label: { ru: "Фильм", en: "Feature film" },
    description: {
      ru: "Полнометражный игровой или анимационный фильм.",
      en: "Feature narrative or animated film.",
    },
    icon: "film",
    accent: "#5ad1c8",
    defaultDuration: "90–120 мин",
  },
  audioplay: {
    label: { ru: "Аудиоспектакль", en: "Audio drama" },
    description: {
      ru: "Радиодрама, подкаст или аудиосериал для эфира.",
      en: "Radio drama, podcast or audio series for broadcast.",
    },
    icon: "headphones",
    accent: "#b79cff",
    defaultDuration: "6 × 25 мин",
  },
  stageplay: {
    label: { ru: "Пьеса", en: "Stage play" },
    description: {
      ru: "Театральная постановка с актами и сценами.",
      en: "Theatrical production with acts and scenes.",
    },
    icon: "theater",
    accent: "#ff8fbf",
    defaultDuration: "2 акта",
  },
  novel: {
    label: { ru: "Роман", en: "Novel" },
    description: {
      ru: "Книга, новеллизация или литературный первоисточник.",
      en: "Book, novelisation or literary source.",
    },
    icon: "book-open",
    accent: "#e8d5a8",
    defaultDuration: "80 000 слов",
  },
  comic: {
    label: { ru: "Комикс", en: "Comic book" },
    description: {
      ru: "Графическая история с панелями и раскадровкой.",
      en: "Graphic story with panels and boards.",
    },
    icon: "panels-top-left",
    accent: "#ff8f7a",
    defaultDuration: "24 страницы",
  },
  treatment: {
    label: { ru: "Тритмент", en: "Treatment" },
    description: {
      ru: "Развёрнутое изложение истории для питчинга.",
      en: "Long-form story outline for pitching.",
    },
    icon: "file-text",
    accent: "#9fb4ff",
    defaultDuration: "10–20 страниц",
  },
  text: {
    label: { ru: "Свободный проект", en: "Free-form" },
    description: {
      ru: "Заметки, исследования и тексты без сценарной структуры.",
      en: "Notes, research and free-form text.",
    },
    icon: "pen-line",
    accent: "#b9c0cc",
    defaultDuration: "—",
  },
};

export const PROJECT_STATUS_META: Record<
  ProjectStatus,
  { label: Localized; color: string; dot: string }
> = {
  development: {
    label: { ru: "Разработка", en: "Development" },
    color: "#9fb4ff",
    dot: "#6f86ff",
  },
  preproduction: {
    label: { ru: "Препродакшен", en: "Pre-production" },
    color: "#f0c974",
    dot: "#f0a93b",
  },
  production: {
    label: { ru: "Производство", en: "Production" },
    color: "#ff9d7a",
    dot: "#ff6b4a",
  },
  postproduction: {
    label: { ru: "Постпродакшен", en: "Post-production" },
    color: "#b79cff",
    dot: "#9b7bff",
  },
  delivered: {
    label: { ru: "Сдан", en: "Delivered" },
    color: "#62d6a3",
    dot: "#3fbf88",
  },
  archived: {
    label: { ru: "В архиве", en: "Archived" },
    color: "#8b93a1",
    dot: "#6c7482",
  },
};

export const PROJECT_ROLE_META: Record<
  ProjectRole,
  { label: Localized; description: Localized; color: string; permissions: string[] }
> = {
  owner: {
    label: { ru: "Владелец", en: "Owner" },
    description: { ru: "Полный доступ и управление проектом", en: "Full access and project control" },
    color: "#f0a93b",
    permissions: [
      "project:read",
      "project:update",
      "project:delete",
      "member:manage",
      "script:read",
      "script:write",
      "script:comment",
      "structure:write",
      "character:write",
      "asset:upload",
      "asset:delete",
      "ai:run",
      "ai:approve",
      "version:create",
      "version:restore",
      "export",
      "production:write",
      "integration:manage",
    ],
  },
  producer: {
    label: { ru: "Продюсер", en: "Producer" },
    description: { ru: "Производство, сроки, команда и бюджетные данные", en: "Production, schedule, team and budget" },
    color: "#62d6a3",
    permissions: [
      "project:read",
      "project:update",
      "member:manage",
      "script:read",
      "script:comment",
      "structure:write",
      "asset:upload",
      "ai:run",
      "ai:approve",
      "version:create",
      "export",
      "production:write",
    ],
  },
  director: {
    label: { ru: "Режиссёр", en: "Director" },
    description: { ru: "Визуальный мир, образы, монтаж и утверждение правок", en: "Visual world, casting, edit and approvals" },
    color: "#b79cff",
    permissions: [
      "project:read",
      "project:update",
      "script:read",
      "script:write",
      "script:comment",
      "structure:write",
      "character:write",
      "asset:upload",
      "ai:run",
      "ai:approve",
      "version:create",
      "export",
      "production:write",
    ],
  },
  showrunner: {
    label: { ru: "Шоураннер", en: "Showrunner" },
    description: { ru: "Арка сезона, сценарий и финальное редакторское решение", en: "Season arc, script and final editorial decision" },
    color: "#f0a93b",
    permissions: [
      "project:read",
      "project:update",
      "member:manage",
      "script:read",
      "script:write",
      "script:comment",
      "structure:write",
      "character:write",
      "asset:upload",
      "ai:run",
      "ai:approve",
      "version:create",
      "version:restore",
      "export",
      "production:write",
    ],
  },
  writer: {
    label: { ru: "Сценарист", en: "Writer" },
    description: { ru: "Пишет сцены и реплики, оставляет комментарии, запускает AI-предложения", en: "Writes scenes, comments and runs AI proposals" },
    color: "#5aa9ff",
    permissions: [
      "project:read",
      "script:read",
      "script:write",
      "script:comment",
      "structure:write",
      "character:write",
      "asset:upload",
      "ai:run",
      "version:create",
      "export",
    ],
  },
  designer: {
    label: { ru: "Художник", en: "Production designer" },
    description: { ru: "Образы персонажей, локации и визуальные референсы", en: "Character looks, locations and visual references" },
    color: "#ff8fbf",
    permissions: [
      "project:read",
      "script:read",
      "script:comment",
      "character:write",
      "asset:upload",
      "ai:run",
      "export",
    ],
  },
  editor: {
    label: { ru: "Редактор", en: "Script editor" },
    description: { ru: "Структурная правка, комментарии и утверждение версий", en: "Structural edits, notes and version approvals" },
    color: "#9fb4ff",
    permissions: [
      "project:read",
      "script:read",
      "script:write",
      "script:comment",
      "structure:write",
      "character:write",
      "ai:run",
      "ai:approve",
      "version:create",
      "export",
    ],
  },
  reviewer: {
    label: { ru: "Рецензент", en: "Reviewer" },
    description: { ru: "Читает, комментирует и утверждает без права правки", en: "Reads, comments and approves without editing" },
    color: "#f0c974",
    permissions: ["project:read", "script:read", "script:comment", "ai:run", "export"],
  },
  viewer: {
    label: { ru: "Наблюдатель", en: "Viewer" },
    description: { ru: "Только чтение материалов проекта", en: "Read-only access to project materials" },
    color: "#8b93a1",
    permissions: ["project:read", "script:read", "export"],
  },
};

export const ORG_ROLE_META: Record<
  "owner" | "admin" | "member",
  { label: Localized; color: string }
> = {
  owner: { label: { ru: "Владелец корпорации", en: "Organization owner" }, color: "#f0a93b" },
  admin: { label: { ru: "Администратор", en: "Administrator" }, color: "#b79cff" },
  member: { label: { ru: "Сотрудник", en: "Member" }, color: "#8b93a1" },
};

export const CHARACTER_ROLE_META: Record<
  CharacterRole,
  { label: Localized; color: string }
> = {
  lead: { label: { ru: "Главная роль", en: "Lead" }, color: "#f0a93b" },
  supporting: { label: { ru: "Поддерживающая роль", en: "Supporting role" }, color: "#5aa9ff" },
  guest: { label: { ru: "Эпизодическая роль", en: "Guest role" }, color: "#b79cff" },
  antagonist: { label: { ru: "Антагонист", en: "Antagonist" }, color: "#ff6b6b" },
  mentor: { label: { ru: "Наставник", en: "Mentor" }, color: "#62d6a3" },
  background: { label: { ru: "Фоновая роль", en: "Background role" }, color: "#8b93a1" },
};

export const STRUCTURE_STATUS_META: Record<
  StructureStatus,
  { label: Localized; color: string }
> = {
  idea: { label: { ru: "Идея", en: "Idea" }, color: "#8b93a1" },
  outline: { label: { ru: "Синопсис", en: "Outline" }, color: "#9fb4ff" },
  draft: { label: { ru: "Черновик", en: "Draft" }, color: "#5aa9ff" },
  revision: { label: { ru: "Правка", en: "Revision" }, color: "#f0a93b" },
  review: { label: { ru: "Проверка", en: "Review" }, color: "#b79cff" },
  locked: { label: { ru: "Утверждено", en: "Locked" }, color: "#62d6a3" },
  done: { label: { ru: "Готово", en: "Done" }, color: "#3fbf88" },
};

export const PRODUCTION_STAGE_META: Record<
  ProductionStage,
  { label: Localized; color: string; icon: string }
> = {
  development: { label: { ru: "Разработка", en: "Development" }, color: "#9fb4ff", icon: "lightbulb" },
  script: { label: { ru: "Сценарий", en: "Script" }, color: "#5aa9ff", icon: "pen-line" },
  review: { label: { ru: "Проверка", en: "Review" }, color: "#b79cff", icon: "eye" },
  voice: { label: { ru: "Озвучка", en: "Voice" }, color: "#ff8fbf", icon: "mic" },
  sound: { label: { ru: "Звук", en: "Sound" }, color: "#62d6a3", icon: "audio-lines" },
  edit: { label: { ru: "Монтаж", en: "Edit" }, color: "#f0a93b", icon: "scissors" },
  delivery: { label: { ru: "Сдача", en: "Delivery" }, color: "#ff8f7a", icon: "send" },
};

export const PRODUCTION_STATUS_META: Record<
  ProductionStatus,
  { label: Localized; color: string }
> = {
  todo: { label: { ru: "К выполнению", en: "To do" }, color: "#8b93a1" },
  in_progress: { label: { ru: "В работе", en: "In progress" }, color: "#5aa9ff" },
  blocked: { label: { ru: "Заблокировано", en: "Blocked" }, color: "#ff6b6b" },
  review: { label: { ru: "На проверке", en: "In review" }, color: "#f0a93b" },
  done: { label: { ru: "Готово", en: "Done" }, color: "#62d6a3" },
};

export const BREAKDOWN_CATEGORY_META: Record<
  BreakdownCategory,
  { label: Localized; short: Localized; color: string; icon: string }
> = {
  props: {
    label: { ru: "Реквизит", en: "Props" },
    short: { ru: "Реквизит", en: "Props" },
    color: "#f0a93b",
    icon: "package",
  },
  costume: {
    label: { ru: "Костюмы", en: "Costumes" },
    short: { ru: "Костюмы", en: "Costumes" },
    color: "#ff8fbf",
    icon: "shirt",
  },
  makeup: {
    label: { ru: "Грим", en: "Makeup" },
    short: { ru: "Грим", en: "Makeup" },
    color: "#e8d5a8",
    icon: "sparkles",
  },
  vfx: {
    label: { ru: "VFX", en: "VFX" },
    short: { ru: "VFX", en: "VFX" },
    color: "#b79cff",
    icon: "wand-2",
  },
  stunts: {
    label: { ru: "Трюки", en: "Stunts" },
    short: { ru: "Трюки", en: "Stunts" },
    color: "#ff6b6b",
    icon: "flame",
  },
  extras: {
    label: { ru: "Массовка", en: "Extras" },
    short: { ru: "Массовка", en: "Extras" },
    color: "#62d6a3",
    icon: "users",
  },
  vehicle: {
    label: { ru: "Транспорт", en: "Vehicles" },
    short: { ru: "Транспорт", en: "Vehicles" },
    color: "#5ad1c8",
    icon: "car",
  },
  sound: {
    label: { ru: "Звук", en: "Sound" },
    short: { ru: "Звук", en: "Sound" },
    color: "#9fb4ff",
    icon: "audio-lines",
  },
  other: {
    label: { ru: "Другое", en: "Other" },
    short: { ru: "Другое", en: "Other" },
    color: "#8b93a1",
    icon: "circle-dot",
  },
};

export const AI_AGENT_TEMPLATES: Record<
  AiAgentTemplate,
  {
    label: Localized;
    tagline: Localized;
    role: string;
    emoji: string;
    color: string;
    persona: string;
    instructions: string;
  }
> = {
  showrunner: {
    label: { ru: "Шоураннер", en: "Showrunner" },
    tagline: {
      ru: "Держит арку сезона и целостность истории",
      en: "Guards season arc and story integrity",
    },
    role: "AI-шоураннер",
    emoji: "◈",
    color: "#f0a93b",
    persona:
      "Ты опытный шоураннер сериала. Ты мыслишь аркой сезона, конфликтом и темой. Ты не пишешь красивые реплики ради реплик: каждый эпизод должен двигать историю.",
    instructions:
      "Предлагай решения, которые усиливают конфликт и работают на арку. Всегда указывай, какой бит арки затрагивается. Не меняй характер персонажа без обоснования.",
  },
  character: {
    label: { ru: "Архитектор персонажей", en: "Character architect" },
    tagline: {
      ru: "Следит за целями, ранами и противоречиями героев",
      en: "Tracks goals, wounds and contradictions",
    },
    role: "AI-архитектор персонажей",
    emoji: "◐",
    color: "#ff8fbf",
    persona:
      "Ты драматург-психолог. Ты строишь персонажей через желание, страх, ложь, которой они себя утешают, и цену выбора. Ты держишь их голоса различимыми.",
    instructions:
      "Проверяй соответствие реплик характеру и арке. Если персонаж поступает вне характера — предложи альтернативу или обоснованное изменение арки.",
  },
  dialogue: {
    label: { ru: "Доктор диалога", en: "Dialogue doctor" },
    tagline: {
      ru: "Убирает exposition, усиливает подтекст",
      en: "Cuts exposition, sharpens subtext",
    },
    role: "AI-редактор диалогов",
    emoji: "◇",
    color: "#5aa9ff",
    persona:
      "Ты мастер диалога. Ты веришь, что персонажи говорят не то, что думают. Ты режешь экспозицию, ищешь конфликт в каждой реплике и сохраняешь ритм.",
    instructions:
      "Переписывай реплики короче и конкретнее, сохраняя голос персонажа. Не добавляй новых фактов о мире без запроса.",
  },
  continuity: {
    label: { ru: "Скрипт-супервайзер", en: "Script supervisor" },
    tagline: {
      ru: "Ловит разрывы времени, реквизита и фактов",
      en: "Catches time, prop and fact breaks",
    },
    role: "AI-скрипт-супервайзер",
    emoji: "⌖",
    color: "#62d6a3",
    persona:
      "Ты скрипт-супервайзер. Ты помнишь, кто где был, что на ком надето, кто что знает и сколько времени прошло. Ты не даёшь истории противоречить себе.",
    instructions:
      "Возвращай только конкретные несостыковки с указанием сцены и факта. Не переписывай сцены, если не просят.",
  },
  researcher: {
    label: { ru: "Исследователь", en: "Researcher" },
    tagline: {
      ru: "Проверяет факты, профессии и контекст",
      en: "Checks facts, professions and context",
    },
    role: "AI-исследователь",
    emoji: "⌕",
    color: "#b79cff",
    persona:
      "Ты исследователь и консультант по производственной достоверности. Ты отделяешь проверенный факт от предположения и всегда указываешь источники, если они есть.",
    instructions:
      "Помечай уровень уверенности: факт / вероятно / требует проверки. Не выдумывай источники.",
  },
  coverage: {
    label: { ru: "Ридер", en: "Coverage reader" },
    tagline: {
      ru: "Пишет разбор сценария и оценивает готовность",
      en: "Writes coverage and readiness notes",
    },
    role: "AI-ридер",
    emoji: "◎",
    color: "#f0c974",
    persona:
      "Ты профессиональный ридер телерадиокорпорации. Ты оцениваешь сценарий по структуре, персонажам, диалогу, оригинальности и производственной реализуемости.",
    instructions:
      "Пиши коротко и по делу: что работает, что не работает, что исправить в первую очередь. Оценки — по 10-балльной шкале.",
  },
  "voice-director": {
    label: { ru: "Режиссёр озвучки", en: "Voice director" },
    tagline: {
      ru: "Подбирает голоса персонажей под сцену",
      en: "Casts character voices per scene",
    },
    role: "AI-режиссёр озвучки",
    emoji: "◉",
    color: "#f0a93b",
    persona:
      "Ты режиссёр озвучки. Ты слышишь сцену: кто говорит, в каком состоянии, где нужна тихая реплика, а где крик. Ты выбираешь только из готовых утверждённых голосов персонажей и никогда не клонируешь голос без согласия.",
    instructions:
      "Для каждой говорящей роли предложи один готовый голосовой профиль, эмоцию и короткое обоснование. Если подходящего профиля нет — скажи об этом, не выдумывай голос. Окончательное решение принимает человек.",
  },
  custom: {
    label: { ru: "Свой агент", en: "Custom agent" },
    tagline: { ru: "Настраиваемая роль и инструкции", en: "Custom role and instructions" },
    role: "AI-агент",
    emoji: "✦",
    color: "#9fb4ff",
    persona: "",
    instructions: "",
  },
};

export const DEFAULT_PAGE_SETUP: PageSetup = {
  pageSize: "A4",
  fontFamily: "courier-prime",
  fontSize: 12,
  marginTop: 1,
  marginBottom: 1,
  marginLeft: 1,
  marginRight: 1,
  showSceneNumbers: true,
  numberPages: true,
  includeNotes: false,
  titlePage: true,
};

export const DEFAULT_PROJECT_SETTINGS = {
  page: DEFAULT_PAGE_SETUP,
  revisionColor: "blue" as RevisionColor,
  revisionMode: false,
  aiHumanApproval: true as const,
  language: "ru" as const,
  direction: "ltr" as const,
  structureLabels: { act: "Акт", sequence: "Последовательность", scene: "Сцена" },
};

export const PLATFORM_EVENTS = [
  "document.created",
  "document.updated",
  "document.snapshot",
  "comment.created",
  "comment.resolved",
  "ai.proposal.created",
  "ai.proposal.applied",
  "character.updated",
  "project.member.added",
  "project.exported",
  "production.task.updated",
] as const;
export type PlatformEvent = (typeof PLATFORM_EVENTS)[number];

export const EPISODE_STATUS_META: Record<
  import("./types.js").EpisodeStatus,
  { label: Localized; color: string; description: Localized }
> = {
  outline: { label: { ru: "Синопсис", en: "Outline" }, color: "#9fb4ff", description: { ru: "Есть идея и место в арке", en: "Idea and place in the arc" } },
  draft: { label: { ru: "Черновик", en: "Draft" }, color: "#f0c974", description: { ru: "Первая версия сценария", en: "First script draft" } },
  review: { label: { ru: "На проверке", en: "Review" }, color: "#f0a93b", description: { ru: "Ждёт редакторского решения", en: "Waiting for editorial approval" } },
  approved: { label: { ru: "Утверждена", en: "Approved" }, color: "#62d6a3", description: { ru: "Можно запускать производство", en: "Ready for production" } },
  locked: { label: { ru: "Заблокирована", en: "Locked" }, color: "#8b93a1", description: { ru: "Финальная версия, правки запрещены", en: "Final version, no edits" } },
};

export const API_TOKEN_SCOPES = [
  "project:read",
  "project:write",
  "member:manage",
  "document:read",
  "document:write",
  "comment:write",
  "structure:write",
  "character:write",
  "asset:read",
  "asset:write",
  "ai:run",
  "ai:approve",
  "version:restore",
  "export",
  "production:read",
  "production:write",
  "integration:manage",
  "webhook:manage",
  "metrics:read",
] as const;
export type ApiTokenScope = (typeof API_TOKEN_SCOPES)[number];

export const API_SCOPE_META: Record<ApiTokenScope, { label: string; description: string }> = {
  "project:read": { label: "Читать проекты", description: "Список проектов, обзор, поиск, лента" },
  "project:write": { label: "Менять проекты", description: "Создание, настройки, архивирование" },
  "member:manage": { label: "Управлять командой", description: "Приглашения и роли участников" },
  "document:read": { label: "Читать сценарии", description: "Документы, версии и комментарии" },
  "document:write": { label: "Писать сценарии", description: "Правки документов и создание версий" },
  "comment:write": { label: "Комментировать", description: "Создание и изменение комментариев" },
  "structure:write": { label: "Менять структуру", description: "Акты, сцены и биты" },
  "character:write": { label: "Менять библию", description: "Персонажи и локации" },
  "asset:read": { label: "Читать медиа", description: "Метаданные и ссылки на файлы" },
  "asset:write": { label: "Загружать медиа", description: "Загрузка и удаление файлов" },
  "ai:run": { label: "Запускать AI", description: "Предложения AI-агентов" },
  "ai:approve": { label: "Утверждать AI", description: "Применение предложений человеком" },
  "version:restore": { label: "Восстанавливать версии", description: "Откат документа к версии" },
  export: { label: "Экспортировать", description: "DOCX, PDF-макет, Fountain, FDX" },
  "production:read": { label: "Читать производство", description: "Задачи и материалы к сдаче" },
  "production:write": { label: "Вести производство", description: "Задачи, статусы и сроки" },
  "integration:manage": { label: "Настраивать интеграции", description: "S3, AI, SSO и API-токены" },
  "webhook:manage": { label: "Управлять webhooks", description: "Подписки на события" },
  "metrics:read": { label: "Читать метрики", description: "Prometheus-метрики и health для мониторинга" },
};
